import { memo } from 'react';
import Head from 'next/head';
import MiscLocale from 'locale/en/misc.json';
import { buildAggregateRating } from 'lib/utils/build-aggregate-rating';

const PAGE_TITLE = 'Daily Essentials Duo';
const PAGE_DESCRIPTION =
  'The Daily Essentials Duo pairs Seed’s DS-01® Daily Synbiotic—a broad-spectrum 24-strain probiotic and prebiotic—with the DM-02® Daily Multivitamin’s 20 essential vitamins and minerals for comprehensive daily gut and whole-body support.';

const DUO_URL = 'https://seed.com/daily-essentials-duo';
const DUO_IMAGE =
  'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757943559/npd/Daily%20Essentials%20Duo.png';

// Constituent products of the bundle. Sold as a single discounted offer, but
// described individually per Google/schema.org bundle guidance (includesObject
// + isRelatedTo). Metadata mirrors the DS-01 and DM-02 standalone PDP heads.
const DS01 = {
  name: 'DS-01 Daily Synbiotic (Probiotic + Prebiotic)',
  sku: 'SYN-WK',
  gtin: '860340001620',
  url: 'https://seed.com/daily-synbiotic',
  image:
    'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517363/structured-data/og-ds.png',
};

const DM02 = {
  name: 'DM-02 Daily Multivitamin',
  sku: 'DM-WK',
  gtin: '850058995168',
  url: 'https://seed.com/daily-multivitamin',
  image:
    'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517366/structured-data/og-dm.png',
};

type DuoPageHeadProps = {
  // Discounted bundle price (DS-01 + DM-02) computed from live product data,
  // matching the price displayed in the buybox.
  bundlePrice: string;
  faqSchema?: object;
  yotpoAverageScore?: string | number | null;
  yotpoTotalReviews?: string | number | null;
};

const buildComponentProduct = (product: typeof DS01) => ({
  '@type': 'Product',
  name: product.name,
  sku: product.sku,
  gtin: product.gtin,
  image: product.image,
  url: product.url,
  brand: { '@type': 'Brand', name: 'Seed' },
});

const DuoPageHead = ({
  bundlePrice,
  faqSchema,
  yotpoAverageScore,
  yotpoTotalReviews,
}: DuoPageHeadProps) => {
  const aggregateRating = buildAggregateRating(
    yotpoAverageScore,
    yotpoTotalReviews,
  );

  const structuredData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: PAGE_TITLE,
    image: [DUO_IMAGE, DS01.image, DM02.image],
    description: PAGE_DESCRIPTION,
    sku: 'ds-duo',
    ...(aggregateRating ? { aggregateRating } : {}),
    brand: {
      '@type': 'Brand',
      name: 'Seed',
    },
    isRelatedTo: [
      { '@type': 'Product', name: DS01.name, url: DS01.url },
      { '@type': 'Product', name: DM02.name, url: DM02.url },
    ],
    offers: {
      '@type': 'Offer',
      url: DUO_URL,
      price: bundlePrice,
      priceCurrency: 'USD',
      itemCondition: 'http://schema.org/NewCondition',
      availability: 'http://schema.org/InStock',
      acceptedPaymentMethod: [
        { '@type': 'PaymentMethod', name: 'Credit Card' },
        { '@type': 'PaymentMethod', name: 'PayPal' },
        { '@type': 'PaymentMethod', name: 'Apple Pay' },
      ],
      // The bundle's purchasable offer is a single discounted price; the two
      // constituent products are listed for semantic accuracy only.
      includesObject: [
        {
          '@type': 'TypeAndQuantityNode',
          amountOfThisGood: 1,
          unitCode: 'EA',
          typeOfGood: buildComponentProduct(DS01),
        },
        {
          '@type': 'TypeAndQuantityNode',
          amountOfThisGood: 1,
          unitCode: 'EA',
          typeOfGood: buildComponentProduct(DM02),
        },
      ],
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: bundlePrice,
        priceCurrency: 'USD',
        priceType: 'http://schema.org/MSRP',
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'USD',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          businessDays: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'http://schema.org/Monday',
              'http://schema.org/Tuesday',
              'http://schema.org/Wednesday',
              'http://schema.org/Thursday',
              'http://schema.org/Friday',
            ],
          },
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: '0',
            maxValue: '1',
            unitCode: 'd',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: '3',
            maxValue: '8',
            unitCode: 'd',
          },
        },
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        merchantReturnLink: `https://help${process.env.NEXT_PUBLIC_COOKIE_DOMAIN}`,
        description:
          '30 Day Money-Back Guarantee on First Order without Product Return',
        merchantReturnDays: '30',
        returnFees: '0',
      },
    },
  };

  const title = `${MiscLocale.brand} • ${PAGE_TITLE}`;

  return (
    <Head>
      <title>{title}</title>
      <meta name='description' content={PAGE_DESCRIPTION} />
      <link rel='canonical' href={DUO_URL} />
      <meta property='og:locale' content='en_US' />
      <meta property='og:site_name' content='Seed' />
      <meta property='og:title' content={title} />
      <meta property='og:description' content={PAGE_DESCRIPTION} />
      <meta property='og:url' content={DUO_URL} />
      <meta property='og:type' content='website' />
      <meta property='og:image' content={DUO_IMAGE} />
      <meta property='og:image:alt' content={PAGE_TITLE} />

      {/* <!-- Twitter --> */}
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={PAGE_DESCRIPTION} />
      <meta name='twitter:url' content={DUO_URL} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:site' content='@seedhealth' />
      <meta name='twitter:image' content={DUO_IMAGE} />
      <meta name='twitter:image:alt' content={PAGE_TITLE} />
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      {faqSchema && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
    </Head>
  );
};

export default memo(DuoPageHead);
